<template>
    <Modal @close="close" :width="320" class="availability-preview-modal" closeOnEscape dismissible hide-footer>
        <div class="pt-3">
            <button class="availability-preview-modal__close btn btn-icon" :title="$t('Generic.close')" @click="close">
                <IconSvg filepath="close.svg" />
            </button>

            <SectionHeader class="text-center"> {{ $t('ProductDetails.availabilityPreview.heading') }} </SectionHeader>

            <div v-if="errors.length" class="availability-preview-modal__errors alert alert-danger" role="alert">
                <ul class="list-unstyled mb-0">
                    <li v-for="(error, index) in errors" :key="'error' + index" v-html="error"></li>
                </ul>
            </div>

            <div v-if="!isLoading">
                <select
                    v-model="variantId"
                    class="form-control input-gray custom-select mb-3 text-truncate"
                    aria-label="Produkt-variant"
                    :disabled="!!errors.length"
                >
                    <option disabled value="">{{ $t('ProductDetails.availabilityPreview.chooseOption') }}</option>
                    <option v-for="variant in variants" :key="'variant' + variant.id" :value="variant.id">
                        {{ variant.title }}
                    </option>
                </select>

                <Calendar ref="calendar" :available-dates="vacancies" @update:selected="showTooltip" />

                <p class="mt-2 small muted">{{ $t('ProductDetails.availabilityPreview.legalInfo') }}</p>
            </div>

            <div v-else style="height: 373px" class="availability-preview-modal__spinner-block">
                <IconSvg filepath="spinner.svg"></IconSvg>
            </div>
        </div>
    </Modal>
</template>

<script>
import Modal from '@/components/Modal.vue';
import Calendar from '@/components/product/Calendar.vue';
import SectionHeader from '@/components/SectionHeader.vue';
import tooltip from '@/directives/tooltip';

export default {
    name: 'ProductAvailabilityModal',
    components: { SectionHeader, Calendar, Modal },

    data() {
        return {
            variants: [],
            isLoading: false,
            errors: [],
            variantId: ''
        };
    },

    props: {
        productId: {
            type: [Number, String],
            required: true
        },

        selectedVariantId: [Number, String]
    },

    computed: {
        vacancies() {
            if (this.variantId === '') {
                return [];
            }

            const foundVariantMatch = this.variants.find(v => v.id === this.variantId);
            if (foundVariantMatch) {
                return foundVariantMatch.vacancies;
            }

            return [];
        },

        tooltipOptions() {
            const message = this.$t('ProductDetails.availabilityPreview.tooltip');
            return {
                value: {
                    delay: 0,
                    content: message,
                    zIndex: 100000,
                    eager: true,
                    helpCursor: false,
                    useAnchorAPI: true
                }
            };
        }
    },

    methods: {
        close() {
            this.$emit('close');
        },

        async getVacanciesByVariants() {
            this.isLoading = true;
            this.errors = [];

            try {
                const { data } = await this.$axios.get(`/shop/product-details/${this.productId}/vacancies-by-variants`);

                if (!data || !data.length) {
                    this.errors = [this.$t('ProductDetails.availabilityPreview.noAvailability')];
                } else {
                    this.variants = data;

                    if (this.selectedVariantId != null && this.variants.some(v => v.id == this.selectedVariantId)) {
                        this.variantId = this.selectedVariantId;
                    } else if (this.variants.length === 1) {
                        this.variantId = this.variants[0].id;
                    }
                }
            } catch (e) {
                this.errors = Array.isArray(e) ? e : [e];
            } finally {
                this.isLoading = false;
            }
        },

        showTooltip() {
            const focusedCell = document.activeElement;

            if (focusedCell && focusedCell.classList.contains('calendar__table-date')) {
                tooltip.bind(focusedCell, this.tooltipOptions);

                focusedCell.addEventListener(
                    'blur',
                    () => {
                        tooltip.unbind(focusedCell);
                    },
                    {
                        once: true
                    }
                );
            }
        }
    },
    created() {
        this.getVacanciesByVariants();
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
.availability-preview-modal {
    &__spinner-block {
        display: grid;
        place-items: center;
    }

    &__close {
        position: absolute;
        right: 5px;
        top: 5px;
    }

    .muted {
        color: variables.$muted;
    }
}
</style>
